<template lang="html">
  <div class="xzreport text--primary printable">
    <div class="xzreport__section">
      <div class="xzreport__section-title text-center">
        {{type}}-report Total
      </div>
      <div class="xzreport__section-value">
        <div>{{date}}</div>
        <div v-if="pointOfSale">
          Point Of Sale: {{pointOfSale}}
        </div>
        <div v-if="serialNumber">
          Serial number: {{serialNumber}}
        </div>
      </div>
    </div>

    <div class="xzreport__section">
      <div class="xzreport__section-title text-center">
        {{orgName}}
      </div>
      <div class="xzreport__section-value">
        org.nr. {{orgNumber}}
      </div>
    </div>

    <v-divider class="mt-2 mb-4" />

    <div v-if="unfinishedBillsCount != null" class="d-flex">
      <div class="mr-1">Number of unfinished sales:</div>
      <v-spacer />
      <div>{{unfinishedBillsCount}}</div>
    </div>
    <div v-if="unfinishedItemsCount != null" class="d-flex">
      <div class="mr-1">Number of unfinished sales items:</div>
      <v-spacer />
      <div>{{unfinishedItemsCount.toFixed(0)}}</div>
    </div>
    <div v-if="unfinishedAmount != null" class="d-flex">
      <div class="mr-1">Unfinished sales amount (gross):</div>
      <v-spacer />
      <div>{{unfinishedAmount.toFixed(2)}} SEK</div>
    </div>
    <div v-if="receiptsCount != null" class="d-flex">
      <div class="mr-1">Number of receipts:</div>
      <v-spacer />
      <div>{{receiptsCount}}</div>
    </div>
    <div v-if="receiptCopiesCount != null" class="d-flex">
      <div class="mr-1">Number of receipt copies:</div>
      <v-spacer />
      <div>{{receiptCopiesCount}}</div>
    </div>

    <div class="d-flex">
      <div class="mr-1">Discounts:</div>
      <v-spacer />
      <div>{{totalDiscount.toFixed(2)}} SEK</div>
    </div>

    <v-divider class="mt-4 mb-4"/>

    <div class="d-flex">
      <div class="mr-1">Registered not completed sales:</div>
      <v-spacer />
      <div>{{incompletePurchasePayments.reduce((ac, p) => ac + parseFloat(p.amount), 0).toFixed(2)}} SEK</div>
    </div>
    <div class="d-flex">
      <div class="mr-1">Reversal of not completed sales:</div>
      <v-spacer />
      <div>{{reversalRefundPayments.reduce((ac, p) => ac + parseFloat(p.amount) * -1, 0).toFixed(2)}} SEK</div>
    </div>
    <div class="d-flex">
      <div class="mr-1">Registered not completed refunds:</div>
      <v-spacer />
      <div>{{incompleteRefundPayments.reduce((ac, p) => ac + parseFloat(p.amount), 0).toFixed(2)}} SEK</div>
    </div>
    <div class="d-flex">
      <div class="mr-1">Reversal of not completed refunds:</div>
      <v-spacer />
      <div>{{reversalPurchasePayments.reduce((ac, p) => ac + parseFloat(p.amount) * -1, 0).toFixed(2)}} SEK</div>
    </div>

    <v-divider class="mt-4 mb-6"/>

    <template v-for="section in byPaymentMethod">
      <div class="xzreport__section">
        <div class="xzreport__section-title">
          {{section.title}}
        </div>
        <div class="xzreport__section-data">
          <table class="bold-last">
            <tbody>
              <tr v-for="group in section.data" :key="group.method">
                <td>{{group.method}}</td>
                <td>{{group.payments.length}}</td>
                <td class="no-break">{{group.payments.reduce((a, payment) => a + payment.amount, 0).toFixed(2)}}&nbsp;SEK</td>
              </tr>
              <tr>
                <td>Total</td>
                <td>{{section.data.reduce((a, group) => a + group.payments.length, 0)}}</td>
                <td class="no-break">{{section.data.reduce((a1, group) => a1 + group.payments.reduce((a2, payment) => a2 + payment.amount, 0), 0).toFixed(2)}}&nbsp;SEK</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <v-divider class="mt-2 mb-6"/>

    <template v-for="section in byItemgroup">
      <div class="xzreport__section">
        <div class="xzreport__section-title">
          {{section.title}}
        </div>
        <div class="xzreport__section-data">
          <table>
            <tbody v-if="section.data.length == 0">
              <tr class="font-weight-regular">
                <td>-</td>
                <td>-</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="group in section.data" :key="group.itemgroupId">
                <td>{{group.itemgroupName || 'Unknown'}}</td>
                <td>{{group.items.reduce((a, item) => a + 1/(item.splitCount || 1) , 0).toFixed(0)}}</td>
                <td class="no-break">{{group.items.reduce((a, item) => a + getNet(item.price, item.vat), 0).toFixed(2)}}&nbsp;SEK</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <v-divider class="mt-2 mb-6"/>

    <template v-for="section in byVat">
      <div class="xzreport__section">
        <div class="xzreport__section-title">
          {{section.title}}
        </div>
        <div class="xzreport__section-data">
          <table>
            <tbody v-if="section.data.length == 0">
              <tr class="font-weight-regular">
                <td>-</td>
                <td>-</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr v-for="group in section.data" :key="group.vat">
                <td>VAT {{group.vat}}%</td>
                <td class="no-break">{{group.items.reduce((a, item) => a + getNet(item.price, item.vat), 0).toFixed(2)}}&nbsp;SEK</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <v-divider class="mt-2 mb-6"/>

    <div class="xzreport__section">
      <div class="xzreport__section-title">
        Total
      </div>
      <div class="xzreport__section-data">
        <table class="bold-last">
          <tbody>
            <tr>
              <td>Total net</td>
              <td class="no-break">{{totalNet.toFixed(2)}}&nbsp;SEK</td>
            </tr>
            <tr v-for="group in totalVats" :key="group.vat">
              <td>VAT {{group.vat}}%</td>
              <td class="no-break">{{group.amount.toFixed(2)}}&nbsp;SEK</td>
            </tr>
            <tr>
              <td>Total gross</td>
              <td class="no-break">{{totalGross.toFixed(2)}}&nbsp;SEK</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';

export default {
  name: 'XZReport',
  props: {
    type: {
      type: String
    },
    payments: {
      type: Array
    },
    date: {
      type: String
    },
    orgName: {
      type: String
    },
    orgNumber: {
      type: String
    },
    serialNumber: {
      type: String
    },
    pointOfSale: {
      type: String
    },
    unfinishedItemsCount: {
      type: Number
    },
    unfinishedBillsCount: {
      type: Number
    },
    unfinishedAmount: {
      type: Number
    },
    receiptsCount: {
      type: Number
    },
    receiptCopiesCount: {
      type: Number
    }
  },
  data () {
    return {
      now: moment().format('YYYY-MM-DD HH:mm:ss')
    };
  },
  watch: {
    payments: {
      immediate: true,
      handler() {
        this.now = moment().format('YYYY-MM-DD HH:mm:ss');
      }
    }
  },
  computed: {
    byPaymentMethod() {
      return [
        {
          title: 'Sales by payment method',
          data: this.purchasesByPaymentMethod
        },
        {
          title: 'Refunds by payment method',
          data: this.refundsByPaymentMethod
        }
      ];
    },
    byItemgroup() {
      return [
        {
          title: 'Sales by item group (net)',
          data: this.purchaseItemsByItemgroup
        },
        {
          title: 'Refunds by item group (net)',
          data: this.refundItemsByItemgroup
        }
      ];
    },
    byVat() {
      return [
        {
          title: 'Sales by VAT rate (net)',
          data: this.purchaseItemsByVat
        },
        {
          title: 'Refunds by VAT rate (net)',
          data: this.refundItemsByVat
        }
      ];
    },
    reversalPurchasePayments () {
      return this.payments.filter(
        (p) => (
          (!p.transactionType || p.transactionType === 'PURCHASE')
          && p.reversal
        )
      );
    },
    reversalRefundPayments () {
      return this.payments.filter(
        (p) => (
          p.transactionType === 'REFUND'
          && p.reversal
        )
      );
    },
    purchasePayments () {
      return this.payments.filter(
        (p) => (
          (!p.transactionType || p.transactionType === 'PURCHASE')
          && !p.reversal
        )
      );
    },
    refundPayments () {
      return this.payments.filter(
        (p) => (
          p.transactionType === 'REFUND'
          && !p.reversal
        )
      );
    },
    completePurchasePayments () {
      return this.purchasePayments.filter(
        (p) => p.status === 'PAID' || p.status === 'REDEEMED'
      );
    },
    incompletePurchasePayments () {
      var paymentIds = this.completePurchasePayments.map((p) => p.id);
      return this.purchasePayments.filter(
        (p) => paymentIds.indexOf(p.id) === -1
      );
    },
    completeRefundPayments () {
      return this.refundPayments.filter(
        (p) => p.status === 'DEBITED'
      );
    },
    incompleteRefundPayments () {
      var paymentIds = this.completeRefundPayments.map((p) => p.id);
      return this.refundPayments.filter(
        (p) => paymentIds.indexOf(p.id) === -1
      );
    },
    purchaseItems () {
      var result = [];
      this.completePurchasePayments.forEach((payment) => {
        var items = payment.items || [];
        result = result.concat(items);
      });
      return result;
    },
    refundItems () {
      var result = [];
      this.completeRefundPayments.forEach((payment) => {
        var items = payment.items || [];
        result = result.concat(items);
      });
      return result;
    },
    purchasesByPaymentMethod () {
      return this.getPaymentsByPaymentMethod(this.completePurchasePayments);
    },
    refundsByPaymentMethod () {
      return this.getPaymentsByPaymentMethod(this.completeRefundPayments);
    },
    purchaseItemsByItemgroup () {
      return this.getItemsByItemgroup(this.purchaseItems);
    },
    refundItemsByItemgroup () {
      return this.getItemsByItemgroup(this.refundItems);
    },
    purchaseItemsByVat () {
      return this.getItemsByVat(this.purchaseItems);
    },
    refundItemsByVat () {
      return this.getItemsByVat(this.refundItems);
    },
    purchaseTotalNet () {
      return this.purchaseItems
        .reduce((a, item) => a + this.getNet(item.price, item.vat), 0);
    },
    refundTotalNet () {
      return this.refundItems
        .reduce((a, item) => a + this.getNet(item.price, item.vat), 0);
    },
    purchaseTotalGross () {
      return this.purchaseItems.reduce((a, item) => a + item.price, 0);
    },
    refundTotalGross () {
      return this.refundItems.reduce((a, item) => a + item.price, 0);
    },
    totalNet () {
      return this.purchaseTotalNet - this.refundTotalNet;
    },
    totalGross () {
      return this.purchaseTotalGross - this.refundTotalGross;
    },
    totalVats () {
      var itemsByVat = {};
      this.purchaseItemsByVat.forEach((group) => {
        if (itemsByVat[group.vat] == undefined) {
          itemsByVat[group.vat] = {
            purchaseItems: [],
            refundItems: []
          }
        }
        itemsByVat[group.vat].purchaseItems = group.items;
      });
      this.refundItemsByVat.forEach((group) => {
        if (itemsByVat[group.vat] == undefined) {
          itemsByVat[group.vat] = {
            purchaseItems: [],
            refundItems: []
          }
        }
        itemsByVat[group.vat].refundItems = group.items;
      });
      return Object.keys(itemsByVat).sort().map((vat) => {
        var purchaseAmount = itemsByVat[vat].purchaseItems.reduce((a, item) => a + this.getVAT(item.price, item.vat), 0);
        var refundAmount = itemsByVat[vat].refundItems.reduce((a, item) => a + this.getVAT(item.price, item.vat), 0);
        return {
          vat,
          amount: purchaseAmount - refundAmount
        };
      });
    },
    totalDiscount() {
      var result = 0;
      this.purchaseItems.forEach((item) => {
        result += item.discount || 0;
      });
      this.refundItems.forEach((item) => {
        result -= item.discount || 0;
      });
      return result;
    }
  },
  methods: {
    getNet (amount, vat) {
      // Get amount excl. VAT
      return amount / (1 + vat/100.0);
    },
    getVAT (amount, vat) {
      // Get VAT amount
      return this.getNet(amount, vat) * (vat/100.0);
    },
    getPaymentsByPaymentMethod (payments) {
      var result = {};
      payments.forEach((payment) => {
        var paymentType = payment.paymentType || 'Unknown';
        if (!(paymentType in result)) {
          result[paymentType] = [];
        }
        result[paymentType].push(payment);
      });
      result = Object.keys(result)
        .sort()
        .map((key) => {
          return {
            method: key,
            payments: result[key]
          };
        });
      return result;
    },
    getItemsByItemgroup (items) {
      var result = {};
      items.forEach((item) => {
        var itemgroupId = item.itemgroupId || 'Unknown';
        if (!(itemgroupId in result)) {
          result[itemgroupId] = [];
        }
        result[itemgroupId].push(item);
      });
      result = Object.keys(result).map((key) => {
        return {
          itemgroupName: result[key][0].itemgroupName,
          itemgroupId: result[key][0].itemgroupId,
          vat: result[key][0].vat,
          items: result[key]
        }
      });
      this.sortArrayByKey(result, 'itemgroupName');
      return result;
    },
    getItemsByVat (items) {
      var result = {};
      items.forEach((item) => {
        var vat = '' + (item.vat || 'Unknown');

        if (!(vat in result)) {
          result[vat] = [];
        }
        result[vat].push(item);
      });
      result = Object.keys(result).map((key) => {
        return {
          vat: result[key][0].vat,
          items: result[key]
        }
      });
      this.sortArrayByKey(result, 'vat');
      return result;
    },
    sortArrayByKey (array, key) {
      array.sort((a, b) => {
        if (a[key] > b[key]) {
          return 1;
        } else if (a[key] < b[key]) {
          return -1;
        }
        return 0;
      });
      return array;
    }
  }
}
</script>

<style lang="css" scoped>
.xzreport-meta {
  font-size: 0.8rem;
  margin-bottom: 1.5rem;
}
.xzreport__section {
  margin-bottom: 1rem;
}
.xzreport__section-title {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.2rem;
}
.xzreport__section-title.text-center {
  text-align: center;
}
.xzreport__section-value {
  font-size: 0.9rem;
  text-align: center;
}
.xzreport__section-data {}
.xzreport__section-data table {
  width: 100%;
}
.xzreport__section-data table td:not(:last-of-type) {
  padding-right: 0.6rem;
}
.xzreport__section-data table td:not(:first-of-type) {
  text-align: right;
}
.xzreport__section-data table td.no-break {
  white-space: nowrap;
}
.xzreport__section-data table.bold-last tr:last-of-type {
  font-weight: bold;
}
</style>
